import _getAppName from "../functions/helpers/getAppName.js";
import _quillClasses from "../dict/quillClasses.js";
import _flexbox from "../dict/flexbox.js";
import _fixOverlap from "../functions/eventGroups/fixOverlap.js";
import _autoFormatCheck from "../functions/eventGroups/autoFormatCheck.js";
import _autoFormat from "../functions/eventGroups/autoFormat.js";
import _ttmlFunc from "../functions/profiles/ttmlGeneral.js";
import { XMLParser as _XMLParser } from "fast-xml-parser";
import _eol from "eol";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _convertToPlainTextCustom from "../functions/quill/convertToPlainTextCustom.js";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const convertToPlainTextCustom = _convertToPlainTextCustom;
const convertToHtml = _convertToHtml;
const eol = _eol;
const XMLParser = _XMLParser;
const ttmlFunc = _ttmlFunc;
const autoFormat = _autoFormat;
const autoFormatCheck = _autoFormatCheck;
const fixOverlap = _fixOverlap;
const flexbox = _flexbox;
const quillClasses = _quillClasses;
const getAppName = _getAppName;
const VERTICAL_REGION_PREFIXES = [{
  id: "bottom",
  flex: "end"
}, {
  id: "top",
  flex: "start"
}, {
  id: "center",
  flex: "center"
}];
const HORIZONTAL_REGION_SUFFIXES = [{
  id: "Left",
  flex: "start"
}, {
  id: "Right",
  flex: "end"
}, {
  id: "Center",
  flex: "center"
}];
const STANDALONE_REGION_POSITIONS = {
  top: {
    xPos: "center",
    yPos: "start"
  },
  bottom: {
    xPos: "center",
    yPos: "end"
  },
  center: {
    xPos: "center",
    yPos: "center"
  },
  left: {
    xPos: "start",
    yPos: "center"
  },
  right: {
    xPos: "end",
    yPos: "center"
  }
};
const getModeFromCellResolution = function (cellResolution) {
  if (cellResolution === "32 15") {
    return "Closed Caption";
  }
  return "Subtitle";
};
const getWindowForMode = function (mode) {
  if (mode === "Closed Caption") {
    return {
      height: 15,
      width: 32,
      xPadding: 10,
      yPadding: 10
    };
  }
  return {
    height: 11,
    width: 42,
    xPadding: 10,
    yPadding: 10
  };
};
const getFontSizeForMode = function (mode) {
  return mode === "Closed Caption" ? 80 : 68;
};
const getCellResolutionForMode = function (mode) {
  return mode === "Closed Caption" ? "32 15" : "42 11";
};
const resolveDecodeMode = function (input, formatOptions) {
  const decodingOptions = getFormatOptions(formatOptions);
  if (decodingOptions["Mode"]) {
    return decodingOptions["Mode"];
  }
  const cellResolutionMatch = input.match(/ttp:cellResolution\s*=\s*["']([^"']+)["']/);
  if (cellResolutionMatch) {
    return getModeFromCellResolution(cellResolutionMatch[1]);
  }
  return "Subtitle";
};
const parseAeRegionId = function (regionId) {
  if (!regionId || typeof regionId !== "string") {
    return null;
  }
  if (STANDALONE_REGION_POSITIONS[regionId]) {
    return {
      ...STANDALONE_REGION_POSITIONS[regionId]
    };
  }
  const sortedVertical = [...VERTICAL_REGION_PREFIXES].sort((a, b) => b.id.length - a.id.length);
  for (const vertical of sortedVertical) {
    if (!regionId.startsWith(vertical.id)) {
      continue;
    }
    const remainder = regionId.slice(vertical.id.length);
    const position = {
      yPos: vertical.flex
    };
    if (remainder === "") {
      position.xPos = "center";
      return position;
    }
    for (const horizontal of HORIZONTAL_REGION_SUFFIXES) {
      if (remainder === horizontal.id) {
        position.xPos = horizontal.flex;
        return position;
      }
    }
    return null;
  }
  return null;
};
const alignmentFromXPos = function (xPos) {
  if (xPos === "start") {
    return "left";
  }
  if (xPos === "end") {
    return "right";
  }
  return "center";
};
const decodeParagraph = function (paragraph, regions, styles, options, useClockTime) {
  if (!paragraph || paragraph["#text"] === undefined || paragraph["#text"] === null) {
    return null;
  }
  let region = false;
  let style = false;
  if (paragraph["@_region"]) {
    region = regions.find(regionEl => regionEl["@_xml:id"] === paragraph["@_region"]) || false;
  }
  if (paragraph["@_style"]) {
    style = styles.find(styleEl => styleEl["@_xml:id"] === paragraph["@_style"]) || false;
  }
  let event = ttmlFunc.multiLine.decodeSubtitle(paragraph, region, style, options.frameRate, options.dropFrame, useClockTime, options.window);
  const regionPosition = parseAeRegionId(paragraph["@_region"]);
  if (regionPosition) {
    event.xPos = regionPosition.xPos;
    event.yPos = regionPosition.yPos;
    event.alignment = alignmentFromXPos(regionPosition.xPos);
    let html = ttmlFunc.multiLine.formatText(paragraph["#text"].toString());
    event.text = convertToHtml(html, [quillClasses.align[event.alignment]]);
  }
  return event;
};
export default {
  decode: function (input, options) {
    let events = [],
      regions = [],
      styles = [];
    const xmlToJson = new XMLParser({
      stopNodes: ["*.p"],
      numberParseOptions: {
        skipLike: /[0-9]+/
      },
      ignoreAttributes: false
    });
    let fileJson = xmlToJson.parse(input);
    if (!fileJson.tt) {
      fileJson = {
        tt: fileJson
      };
    }
    let mode = resolveDecodeMode(input, options.formatOptions);
    options.window = getWindowForMode(mode);
    let timeBase = ttmlFunc.getTimeBaseValue(input) || "media";
    let useClockTime = timeBase === "media" || timeBase === "clock";
    if (fileJson.tt.head && fileJson.tt.head.layout && fileJson.tt.head.layout.region) {
      if (Array.isArray(fileJson.tt.head.layout.region)) {
        regions = fileJson.tt.head.layout.region;
      } else {
        regions = [fileJson.tt.head.layout.region];
      }
    }
    if (fileJson.tt.head && fileJson.tt.head.styling && fileJson.tt.head.styling.style) {
      if (Array.isArray(fileJson.tt.head.styling.style)) {
        styles = fileJson.tt.head.styling.style;
      } else {
        styles = [fileJson.tt.head.styling.style];
      }
    }
    const pushParagraph = function (paragraph) {
      let event = decodeParagraph(paragraph, regions, styles, options, useClockTime);
      if (event) {
        events.push(event);
      }
    };
    if (Array.isArray(fileJson.tt.body.div)) {
      fileJson.tt.body.div.forEach(paragraphGroup => {
        if (Array.isArray(paragraphGroup.p)) {
          paragraphGroup.p.forEach(pushParagraph);
        } else if (paragraphGroup.p) {
          pushParagraph(paragraphGroup.p);
        }
      });
    } else if (fileJson.tt.body && fileJson.tt.body.div) {
      if (Array.isArray(fileJson.tt.body.div.p)) {
        fileJson.tt.body.div.p.forEach(pushParagraph);
      } else if (fileJson.tt.body.div.p) {
        pushParagraph(fileJson.tt.body.div.p);
      }
    }
    return events;
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions);
    let mode = encodingOptions["Mode"] || "Subtitle";
    let languageCode = encodingOptions["Language Code"] || "en";
    let fontFamily = mode === "Subtitle" ? "proportionalSansSerif" : "monospace";
    let backgroundColor = mode === "Subtitle" ? "transparent" : "black";
    let fontWeight = "normal";
    let fontStyle = "normal";
    let fontColor = "white";
    let textOutline = mode === "Subtitle" ? "black 4%" : "none";
    let timeBase = "media";
    let fontSize = getFontSizeForMode(mode);
    options.window = getWindowForMode(mode);
    let frameRateMultiplier = ttmlFunc.frameRateMultiplierMap[options.frameRate];
    let output = eol.after(`<?xml version="1.0" encoding="UTF-8"?>`);
    output += eol.after(`<tt xml:lang="${languageCode}"
        xmlns="http://www.w3.org/ns/ttml"
        xmlns:tts="http://www.w3.org/ns/ttml#styling"
        xmlns:ttm="http://www.w3.org/ns/ttml#metadata"
        xmlns:smpte="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt"
        xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608"
        xmlns:ttp="http://www.w3.org/ns/ttml#parameter"
        ttp:timeBase="${timeBase}"
        ttp:frameRate="${ttmlFunc.frameRateMap[options.frameRate]}"
        ttp:frameRateMultiplier="${frameRateMultiplier}"
        ttp:dropMode="${options.dropFrame ? "dropNTSC" : 'nonDrop'}"
        ttp:cellResolution="${getCellResolutionForMode(mode)}">`);
    output += eol.after(`   <head>`);
    output += eol.after(`       <metadata>`);
    output += eol.after(`           <ttm:desc>${getAppName()}</ttm:desc>`);
    output += eol.after(`           <smpte:information xmlns:m608="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608" origin="http://www.smpte-ra.org/schemas/2052-1/2010/smpte-tt#cea608" mode="Preserved" m608:channel="CC1" m608:programName="Demo" m608:captionService="F1C1CC" />`);
    output += eol.after(`       </metadata>`);
    output += eol.after(`       <styling>
            <style xml:id='basic' tts:color='${fontColor}' tts:backgroundColor='${backgroundColor}' tts:fontFamily='${fontFamily}' tts:fontSize='${fontSize}%' tts:fontWeight="${fontWeight}" tts:fontStyle="${fontStyle}" tts:textOutline="${textOutline}"/>
        </styling>`);
    output += eol.after(`       <layout>
            <region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="before" tts:textAlign="left" xml:id="topLeft" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="center" tts:textAlign="left" xml:id="centerLeft" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" tts:textAlign="left" xml:id="bottomLeft" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="before" tts:textAlign="center" xml:id="topCenter" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="center" tts:textAlign="center" xml:id="centerCenter" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" tts:textAlign="center" xml:id="bottomCenter" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="before" tts:textAlign="right" xml:id="topRight" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="center" tts:textAlign="right" xml:id="centerRight" />
			<region tts:origin="10% 10%" tts:extent="80% 80%" tts:displayAlign="after" tts:textAlign="right" xml:id="bottomRight" />
        </layout>`);
    output += eol.after(`   </head>`);
    output += eol.after(`   <body>`);
    output += eol.after(`       <div>`);

    // When mode is set to "Closed Caption" we need to apply a selective auto-format to the Event Group to limit lines to 32 characters.
    if (mode.toLowerCase() === "closed caption") {
      let selectedEvents = [];
      eventGroup.events.forEach((evt, index) => {
        if (autoFormatCheck(evt, 2, 32)) {
          selectedEvents.push(index);
        }
      });
      eventGroup = autoFormat(eventGroup, 2, 32, 0.5, true, selectedEvents);
      eventGroup = fixOverlap(eventGroup);
    }
    eventGroup.events.forEach(event => {
      let start = tcLib.secToTcMs(event.start, options.frameRate, options.dropFrame).replace(",", ".");
      let end = tcLib.secToTcMs(event.end, options.frameRate, options.dropFrame).replace(",", ".");
      let plainTextCustom = convertToPlainTextCustom(event.text, "\n", false, "strong", "em", "u", true);
      let text = ttmlFunc.multiLine.convertToTtml(plainTextCustom);
      let xRegion = flexbox.flexMap.horizontal[event.xPos];
      let yRegion = flexbox.flexMap.vertical[event.yPos];
      if (!xRegion) {
        xRegion = "center";
      }
      if (!yRegion) {
        if (event.yPos / options.window.height < 0.45) {
          yRegion = "top";
        } else if (event.yPos / options.window.height < 0.65) {
          yRegion = "center";
        } else {
          yRegion = "bottom";
        }
      }
      let region = yRegion + xRegion.charAt(0).toUpperCase() + xRegion.slice(1);
      output += eol.after(`           <p style="basic" begin="${start}" end="${end}" region="${region}">${text}</p>`);
    });
    output += eol.after(`       </div>`);
    output += eol.after(`   </body>`);
    output += eol.after(`</tt>`);
    return output;
  },
  preProcess: {
    encode: function (eventGroup) {
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input.trim());
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup) {
      return eventGroup;
    }
  }
};